import React from 'react';
import { Card, CardContent, Typography, Divider, Box, Grid, Chip } from '@mui/material';

const DivisionSummaryCard = ({ divisionUid, categoriesMap = {}, divisionsMap = {}, adjustedTeamsMap = {}, eventsScores = {}, affiliatedMode = 'none' }) => {
  if (!divisionUid) return null;

  // gather teams in division
  const teams = Object.values(adjustedTeamsMap || {}).filter(t => t.division === divisionUid);
  const numTeams = teams.length;
  const entered = teams.filter(t => Number(t.originalTotal || 0) > 0);
  const enteredCount = entered.length;
  const avgOriginal = enteredCount ? Math.round((entered.reduce((s, t) => s + Number(t.originalTotal || 0), 0) / enteredCount) * 10) / 10 : null;
  const avgAdjusted = enteredCount ? Math.round((entered.reduce((s, t) => s + Number(t.adjustedTotal || 0), 0) / enteredCount) * 10) / 10 : null;
  const bestOriginal = enteredCount ? Math.min(...entered.map(t => Number(t.originalTotal || 0))) : null;
  const bestAdjusted = enteredCount ? Math.min(...entered.map(t => Number(t.adjustedTotal || 0))) : null;

  // breakdown by category within this division
  const byCategory = {};
  teams.forEach(t => {
    const c = t.category || 'unknown';
    if (!byCategory[c]) byCategory[c] = { count: 0, entered: 0, sumOriginal: 0, sumAdjusted: 0, tops: [] };
    byCategory[c].count += 1;
    if (Number(t.originalTotal || 0) > 0) {
      byCategory[c].entered += 1;
      byCategory[c].sumOriginal += Number(t.originalTotal || 0);
      byCategory[c].sumAdjusted += Number(t.adjustedTotal || 0);
      byCategory[c].tops.push(Number(t.originalTotal || 0));
    }
  });

  return (
    <Card sx={{ minWidth: 300 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <Typography variant="h6">{divisionsMap[divisionUid] || divisionUid}</Typography>
          </Box>

          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="subtitle2">Avg (Original)</Typography>
            <Typography variant="h6">{avgOriginal ?? '—'}</Typography>
            <Typography variant="subtitle2">Top (Original)</Typography>
            <Typography variant="h6">{bestOriginal ?? '—'}</Typography>
            {affiliatedMode && affiliatedMode !== 'none' && (
              <>
                <Divider sx={{ my: 0.5 }} />
                <Typography variant="subtitle2">Avg (Adjusted)</Typography>
                <Typography variant="h6">{avgAdjusted ?? '—'}</Typography>
                <Typography variant="subtitle2">Top (Adjusted)</Typography>
                <Typography variant="h6">{bestAdjusted ?? '—'}</Typography>
              </>
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 1 }} />

        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Chip label={`Teams: ${numTeams}`} size="small" />
              <Chip label={`Entered: ${enteredCount}`} size="small" />
              <Chip label={`Avg Adj: ${avgAdjusted ?? '—'}`} size="small" />
              <Chip label={`Top Adj: ${bestAdjusted ?? '—'}`} size="small" />
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2">Categories</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
              {Object.keys(byCategory).length === 0 && <Typography variant="body2">No category data</Typography>}
              {Object.entries(byCategory).map(([cUid, stat]) => {
                const avg = stat.entered ? Math.round((stat.sumOriginal / stat.entered) * 10) / 10 : '—';
                const top = stat.tops.length > 0 ? Math.min(...stat.tops) : '—';
                return <Chip key={cUid} label={`${(categoriesMap[cUid] || cUid)}: ${avg !== '—' ? '~' + avg : '—'} / top ${top}`} size="small" />;
              })}
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default DivisionSummaryCard;
